(() => {
  const TAG_NAME = 'complete-set-section';
  const SOLD_OUT_SUFFIX = ' (Rupture de stock)';

  if (customElements.get(TAG_NAME)) return;

  class CompleteSetSection extends HTMLElement {
    constructor() {
      super();
      this.modal = this.querySelector('[data-complete-set-modal]');
      this.modalBody = this.querySelector('[data-complete-set-modal-body]');
      this.lastGuideTrigger = null;
      this.previousBodyOverflow = '';
      this.activeColorControl = null;
      this.guideCache = new Map();
      this.guideRequests = new Map();
      this.guideRequestId = 0;
      this.guidePrefetchHandle = null;
      this.guidePrefetchMode = null;
      this.boundDocumentClick = this.handleDocumentClick.bind(this);
      this.boundEscape = this.handleEscape.bind(this);
      this.boundHorizontalResize = null;
      this.boundHorizontalScroll = null;
    }

    connectedCallback() {
      this.mountGuideModal();
      this.items = [...this.querySelectorAll('[data-complete-set-item]')];
      this.items.forEach((item) => this.initItem(item));
      this.bindGuideModal();
      this.initHorizontalScroll();
      document.addEventListener('click', this.boundDocumentClick);
      document.addEventListener('keydown', this.boundEscape);
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.boundDocumentClick);
      document.removeEventListener('keydown', this.boundEscape);
      this.guideRequestId += 1;

      if (this.guidePrefetchHandle !== null) {
        if (this.guidePrefetchMode === 'idle' && 'cancelIdleCallback' in window) {
          window.cancelIdleCallback(this.guidePrefetchHandle);
        } else {
          window.clearTimeout(this.guidePrefetchHandle);
        }
        this.guidePrefetchHandle = null;
      }

      if (this.modal && this.modal.getAttribute('aria-hidden') === 'false') {
        document.body.style.overflow = this.previousBodyOverflow;
      }

      if (this.modal && this.modal.parentElement === document.body) {
        this.modal.remove();
      }

      if (this.horizontalTrack && this.boundHorizontalScroll) {
        this.horizontalTrack.removeEventListener('scroll', this.boundHorizontalScroll);
      }
      if (this.boundHorizontalResize) {
        window.removeEventListener('resize', this.boundHorizontalResize);
      }
    }


    mountGuideModal() {
      if (!this.modal) return;

      const guideColor = getComputedStyle(this).getPropertyValue('--complete-set-guide').trim();
      if (guideColor) this.modal.style.setProperty('--complete-set-guide', guideColor);

      if (this.modal.parentElement !== document.body) {
        document.body.appendChild(this.modal);
      }
    }

    initHorizontalScroll() {
      if (this.dataset.layoutMode !== 'horizontal') return;

      const track = this.querySelector('[data-complete-set-track]');
      const previousButton = this.querySelector('[data-complete-set-prev]');
      const nextButton = this.querySelector('[data-complete-set-next]');
      const cards = track ? [...track.querySelectorAll('[data-complete-set-item]')] : [];

      if (!track || cards.length < 2 || !previousButton || !nextButton) return;
      if (track.dataset.completeSetScrollInitialized === 'true') return;

      track.dataset.completeSetScrollInitialized = 'true';
      this.horizontalTrack = track;

      const getCurrentIndex = () => {
        const currentLeft = track.scrollLeft;
        let nearestIndex = 0;
        let nearestDistance = Number.POSITIVE_INFINITY;

        cards.forEach((card, index) => {
          const distance = Math.abs(card.offsetLeft - currentLeft);
          if (distance < nearestDistance) {
            nearestDistance = distance;
            nearestIndex = index;
          }
        });

        return nearestIndex;
      };

      const updateArrows = () => {
        const index = getCurrentIndex();
        previousButton.disabled = index <= 0;
        nextButton.disabled = index >= cards.length - 1;
      };

      const goTo = (index) => {
        const safeIndex = Math.max(0, Math.min(cards.length - 1, index));
        track.scrollTo({
          left: cards[safeIndex].offsetLeft,
          behavior: 'smooth'
        });
      };

      previousButton.addEventListener('click', () => goTo(getCurrentIndex() - 1));
      nextButton.addEventListener('click', () => goTo(getCurrentIndex() + 1));

      let scrollFrame = null;
      this.boundHorizontalScroll = () => {
        if (scrollFrame) cancelAnimationFrame(scrollFrame);
        scrollFrame = requestAnimationFrame(updateArrows);
      };
      this.boundHorizontalResize = () => requestAnimationFrame(updateArrows);

      track.addEventListener('scroll', this.boundHorizontalScroll, { passive: true });
      window.addEventListener('resize', this.boundHorizontalResize);
      requestAnimationFrame(updateArrows);
    }

    normalize(value) {
      return String(value || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .trim()
        .toLowerCase();
    }

    findOptionIndex(optionNames, type) {
      const patterns = type === 'color'
        ? ['couleur', 'color', 'colour', 'colore', 'farbe']
        : ['taille', 'size', 'taglia', 'grosse', 'maat'];

      return optionNames.findIndex((name) => patterns.includes(this.normalize(name)));
    }

    resolveOptionIndexes(optionNames) {
      let colorIndex = this.findOptionIndex(optionNames, 'color');
      let sizeIndex = this.findOptionIndex(optionNames, 'size');

      if (optionNames.length >= 2) {
        if (colorIndex < 0 && sizeIndex >= 0) colorIndex = sizeIndex === 0 ? 1 : 0;
        if (sizeIndex < 0 && colorIndex >= 0) sizeIndex = colorIndex === 0 ? 1 : 0;
        if (colorIndex < 0 && sizeIndex < 0) {
          colorIndex = 0;
          sizeIndex = 1;
        }
      }

      if (optionNames.length === 1) {
        if (colorIndex < 0 && sizeIndex < 0) sizeIndex = 0;
      }

      return { colorIndex, sizeIndex };
    }

    initItem(item) {
      if (item.dataset.completeSetInitialized === 'true') return;
      item.dataset.completeSetInitialized = 'true';

      const jsonNode = item.querySelector('[data-complete-set-product-json]');
      if (!jsonNode) return;

      let data;
      try {
        data = JSON.parse(jsonNode.textContent);
      } catch (error) {
        console.error('ARLAYA Complete Set: données produit invalides.', error);
        return;
      }

      const variants = Array.isArray(data.variants) ? data.variants : [];
      const optionNames = Array.isArray(data.optionNames) ? data.optionNames : [];
      const optionValues = Array.isArray(data.optionValues) ? data.optionValues : [];
      if (!variants.length || !optionNames.length) return;

      const { colorIndex, sizeIndex } = this.resolveOptionIndexes(optionNames);
      const colorField = item.querySelector('[data-complete-set-color-field]');
      const sizeField = item.querySelector('[data-complete-set-size-field]');
      const colorControl = item.querySelector('[data-complete-set-color-control]');
      const colorTrigger = item.querySelector('[data-complete-set-color-trigger]');
      const colorMenu = item.querySelector('[data-complete-set-color-menu]');
      const colorInput = item.querySelector('[data-complete-set-color]');
      const colorLabel = item.querySelector('[data-complete-set-color-label]');
      const colorDot = item.querySelector('[data-complete-set-color-dot]');
      const sizeSelect = item.querySelector('[data-complete-set-size]');
      const variantInput = item.querySelector('[data-complete-set-variant-id]');
      const addButton = item.querySelector('[data-complete-set-add]');
      const addLabel = item.querySelector('[data-complete-set-add-label]');
      const priceNode = item.querySelector('[data-complete-set-price]');
      const compareNode = item.querySelector('[data-complete-set-compare]');
      const imageNode = item.querySelector('.arlaya-complete-set__image');
      const form = item.querySelector('[data-complete-set-form]');

      if (!variantInput || !addButton || !form) return;

      const configuredButtonLabel = String(
        data.buttonLabel
        || addLabel?.textContent
        || addButton.dataset.defaultLabel
        || 'AJOUTER AU PANIER'
      ).trim();
      const soldOutButtonLabel = String(
        data.soldOutLabel
        || addButton.dataset.soldOutLabel
        || 'RUPTURE DE STOCK'
      ).trim();

      addButton.dataset.defaultLabel = configuredButtonLabel;
      addButton.dataset.soldOutLabel = soldOutButtonLabel;

      const initialVariant = variants.find((variant) => String(variant.id) === String(data.initialVariantId))
        || variants.find((variant) => variant.available)
        || variants[0];

      const getValues = (index) => {
        if (index < 0) return [];
        const fromOptions = optionValues[index] && Array.isArray(optionValues[index].values)
          ? optionValues[index].values.map(String)
          : [];
        if (fromOptions.length) return fromOptions;
        return [...new Set(variants.map((variant) => variant.options[index]).filter(Boolean).map(String))];
      };

      const colorValues = getValues(colorIndex);
      const sizeValues = getValues(sizeIndex);
      const fixedOptions = [...initialVariant.options];
      const colorImageMap = new Map();

      if (colorIndex >= 0) {
        variants.forEach((variant) => {
          const colorValue = variant.options[colorIndex];
          if (colorValue && variant.featuredImage && !colorImageMap.has(colorValue)) {
            colorImageMap.set(colorValue, variant.featuredImage);
          }
        });
      }

      if (colorIndex < 0 || !colorValues.length) {
        colorField?.classList.add('is-hidden');
      } else {
        colorInput.value = initialVariant.options[colorIndex] || colorValues[0];
        this.populateColorMenu({
          item,
          colorControl,
          colorTrigger,
          colorMenu,
          colorInput,
          colorLabel,
          colorDot,
          values: colorValues,
          selectedValue: colorInput.value,
          variants,
          colorIndex,
          onSelect: () => update('color')
        });
      }

      if (sizeIndex < 0 || !sizeValues.length) {
        sizeField?.classList.add('is-hidden');
      } else {
        this.populateSizeSelect(sizeSelect, sizeValues, initialVariant.options[sizeIndex]);
      }

      const update = (changedType) => {
        const selectedColor = colorIndex >= 0 ? colorInput.value : null;
        let selectedSize = sizeIndex >= 0 ? sizeSelect.value : null;

        if (colorIndex >= 0) fixedOptions[colorIndex] = selectedColor;

        if (sizeIndex >= 0) {
          this.updateSizeAvailability({
            select: sizeSelect,
            values: sizeValues,
            colorIndex,
            colorValue: selectedColor,
            sizeIndex,
            variants
          });

          const selectedOption = sizeSelect.options[sizeSelect.selectedIndex];
          if (!selectedOption || selectedOption.disabled) {
            const firstAvailable = [...sizeSelect.options].find((option) => !option.disabled);
            if (firstAvailable) sizeSelect.value = firstAvailable.value;
          }

          selectedSize = sizeSelect.value;
          fixedOptions[sizeIndex] = selectedSize;
        }

        this.updateColorMenuAvailability({
          menu: colorMenu,
          variants,
          colorIndex
        });

        let selectedVariant = variants.find((variant) => variant.options.every((option, index) => {
          if (index === colorIndex) return option === selectedColor;
          if (index === sizeIndex) return option === selectedSize;
          return option === fixedOptions[index];
        }));

        if (!selectedVariant) {
          selectedVariant = variants.find((variant) => {
            const colorMatches = colorIndex < 0 || variant.options[colorIndex] === selectedColor;
            const sizeMatches = sizeIndex < 0 || variant.options[sizeIndex] === selectedSize;
            return colorMatches && sizeMatches;
          });
        }

        if (!selectedVariant && changedType === 'color' && colorIndex >= 0) {
          selectedVariant = variants.find((variant) => variant.available && variant.options[colorIndex] === selectedColor);
          if (selectedVariant && sizeIndex >= 0) {
            sizeSelect.value = selectedVariant.options[sizeIndex];
            selectedSize = sizeSelect.value;
          }
        }

        selectedVariant = selectedVariant || variants[0];
        variantInput.value = selectedVariant.id;

        const isAvailable = Boolean(selectedVariant.available);
        addButton.disabled = !isAvailable;
        if (addLabel) {
          addLabel.textContent = isAvailable
            ? configuredButtonLabel
            : soldOutButtonLabel;
        }

        if (priceNode) priceNode.textContent = this.formatMoney(selectedVariant.price, item.dataset.currency);

        if (compareNode) {
          const hasCompare = Number(selectedVariant.compareAtPrice) > Number(selectedVariant.price);
          compareNode.classList.toggle('is-hidden', !hasCompare);
          if (hasCompare) {
            compareNode.textContent = this.formatMoney(selectedVariant.compareAtPrice, item.dataset.currency);
          }
        }

        if (imageNode) {
          const nextImage = selectedVariant.featuredImage
            || (colorIndex >= 0 ? colorImageMap.get(selectedColor) : null);

          if (nextImage) {
            imageNode.src = nextImage;
            imageNode.removeAttribute('srcset');
          }
        }

        if (colorIndex >= 0) {
          this.updateColorTrigger({
            value: colorInput.value,
            labelNode: colorLabel,
            dotNode: colorDot
          });
          this.markSelectedColor(colorMenu, colorInput.value);
        }
      };

      sizeSelect?.addEventListener('change', () => update('size'));

      // Le composant natif Shrine <product-form> gère l'ajout, le rendu et
      // l'ouverture du panier. L'ancien intercepteur personnalisé remplaçait
      // le contenu du drawer avec une réponse de sections incompatible et
      // pouvait ouvrir un panneau entièrement blanc.
      if (!form.closest('product-form') || !customElements.get('product-form')) {
        form.addEventListener('submit', (event) => this.addToCart(event, form, addButton), true);
      }

      update();
    }

    populateSizeSelect(select, values, selectedValue) {
      if (!select) return;
      select.innerHTML = '';
      values.forEach((value) => {
        const option = document.createElement('option');
        option.value = value;
        option.dataset.baseLabel = value;
        option.textContent = value;
        option.selected = value === selectedValue;
        select.appendChild(option);
      });
    }

    updateSizeAvailability({ select, values, colorIndex, colorValue, sizeIndex, variants }) {
      if (!select || sizeIndex < 0) return;

      values.forEach((value) => {
        const option = [...select.options].find((candidate) => candidate.value === value);
        if (!option) return;

        const available = variants.some((variant) => {
          const colorMatches = colorIndex < 0 || variant.options[colorIndex] === colorValue;
          return variant.available && colorMatches && variant.options[sizeIndex] === value;
        });

        option.disabled = !available;
        option.textContent = `${value}${available ? '' : SOLD_OUT_SUFFIX}`;
      });
    }

    populateColorMenu({
      colorControl,
      colorTrigger,
      colorMenu,
      colorInput,
      colorLabel,
      colorDot,
      values,
      selectedValue,
      variants,
      colorIndex,
      onSelect
    }) {
      if (!colorControl || !colorTrigger || !colorMenu || !colorInput) return;

      colorMenu.innerHTML = '';

      values.forEach((value) => {
        const available = variants.some((variant) => variant.available && variant.options[colorIndex] === value);
        const option = document.createElement('button');
        option.type = 'button';
        option.className = 'arlaya-complete-set__swatch-option';
        option.dataset.value = value;
        option.setAttribute('role', 'option');
        option.setAttribute('aria-selected', value === selectedValue ? 'true' : 'false');

        if (!available) {
          option.classList.add('is-unavailable');
          option.disabled = true;
        }

        const swatch = document.createElement('span');
        swatch.className = 'arlaya-complete-set__swatch-dot';
        swatch.style.setProperty('--swatch-background', this.colorBackground(value));
        swatch.setAttribute('aria-hidden', 'true');

        const name = document.createElement('span');
        name.className = 'arlaya-complete-set__swatch-option-name';
        name.textContent = value;

        option.appendChild(swatch);
        option.appendChild(name);

        if (!available) {
          const stock = document.createElement('span');
          stock.className = 'arlaya-complete-set__swatch-stock';
          stock.textContent = SOLD_OUT_SUFFIX;
          option.appendChild(stock);
        }

        option.addEventListener('click', () => {
          if (option.disabled) return;
          colorInput.value = value;
          this.updateColorTrigger({ value, labelNode: colorLabel, dotNode: colorDot });
          this.markSelectedColor(colorMenu, value);
          this.closeColorMenu(colorControl);
          onSelect();
        });

        colorMenu.appendChild(option);
      });

      colorTrigger.addEventListener('click', (event) => {
        event.stopPropagation();
        const shouldOpen = !colorControl.classList.contains('is-open');
        this.closeAllColorMenus();
        if (shouldOpen) this.openColorMenu(colorControl);
      });

      this.updateColorTrigger({ value: selectedValue, labelNode: colorLabel, dotNode: colorDot });
    }

    updateColorMenuAvailability({ menu, variants, colorIndex }) {
      if (!menu || colorIndex < 0) return;

      menu.querySelectorAll('[data-value]').forEach((option) => {
        const value = option.dataset.value;
        const available = variants.some((variant) => variant.available && variant.options[colorIndex] === value);
        option.disabled = !available;
        option.classList.toggle('is-unavailable', !available);

        let stock = option.querySelector('.arlaya-complete-set__swatch-stock');
        if (!available && !stock) {
          stock = document.createElement('span');
          stock.className = 'arlaya-complete-set__swatch-stock';
          stock.textContent = SOLD_OUT_SUFFIX;
          option.appendChild(stock);
        }
        if (available && stock) stock.remove();
      });
    }

    updateColorTrigger({ value, labelNode, dotNode }) {
      if (labelNode) labelNode.textContent = value || 'Choisir';
      if (dotNode) dotNode.style.setProperty('--swatch-background', this.colorBackground(value));
    }

    markSelectedColor(menu, value) {
      if (!menu) return;
      menu.querySelectorAll('[data-value]').forEach((option) => {
        option.setAttribute('aria-selected', option.dataset.value === value ? 'true' : 'false');
      });
    }

    openColorMenu(control) {
      if (!control) return;
      const menu = control.querySelector('[data-complete-set-color-menu]');
      const trigger = control.querySelector('[data-complete-set-color-trigger]');
      control.classList.add('is-open');
      menu.hidden = false;
      trigger.setAttribute('aria-expanded', 'true');
      this.activeColorControl = control;
    }

    closeColorMenu(control) {
      if (!control) return;
      const menu = control.querySelector('[data-complete-set-color-menu]');
      const trigger = control.querySelector('[data-complete-set-color-trigger]');
      control.classList.remove('is-open');
      if (menu) menu.hidden = true;
      trigger?.setAttribute('aria-expanded', 'false');
      if (this.activeColorControl === control) this.activeColorControl = null;
    }

    closeAllColorMenus() {
      this.querySelectorAll('[data-complete-set-color-control].is-open').forEach((control) => {
        this.closeColorMenu(control);
      });
    }

    handleDocumentClick(event) {
      if (this.activeColorControl && !this.activeColorControl.contains(event.target)) {
        this.closeColorMenu(this.activeColorControl);
      }
    }

    colorBackground(value) {
      const normalized = this.normalize(value);
      const colorMap = {
        noir: '#111111', black: '#111111', anthracite: '#3b3b3b',
        blanc: '#ffffff', white: '#ffffff', ivoire: '#f4eee0', ivory: '#f4eee0',
        creme: '#eee2cd', cream: '#eee2cd',
        beige: '#d8c3aa', nude: '#d7b69f', chair: '#d7b69f', peau: '#d7b69f', skin: '#d7b69f',
        taupe: '#9a806f', camel: '#b88450', champagne: '#d8c49f',
        rouge: '#c7353c', red: '#c7353c',
        rose: '#e7a2b6', pink: '#e7a2b6', fuchsia: '#c93683',
        violet: '#74508a', purple: '#74508a', lilas: '#b59ac7', lavender: '#b59ac7',
        bleu: '#315fa8', blue: '#315fa8', marine: '#1f2c49', navy: '#1f2c49', ciel: '#8db8dc',
        vert: '#537b58', green: '#537b58', kaki: '#747652', khaki: '#747652',
        gris: '#9b9b9b', grey: '#9b9b9b', gray: '#9b9b9b',
        marron: '#76513e', brown: '#76513e', moka: '#705247', mocha: '#705247', cafe: '#6d4937',
        bordeaux: '#6e2432', burgundy: '#6e2432',
        jaune: '#e0bd3f', yellow: '#e0bd3f',
        orange: '#db7a35',
        argent: '#c2c4c7', silver: '#c2c4c7',
        or: '#c7a24e', gold: '#c7a24e'
      };

      if (/^#[0-9a-f]{3,8}$/i.test(String(value || '').trim())) return String(value).trim();

      const parts = normalized.split(/[\/,+&-]+/).map((part) => part.trim()).filter(Boolean);
      const mapped = parts.map((part) => {
        if (colorMap[part]) return colorMap[part];
        const containedKey = Object.keys(colorMap).find((key) => part.includes(key));
        return containedKey ? colorMap[containedKey] : null;
      }).filter(Boolean);

      if (mapped.length > 1) {
        const step = 100 / mapped.length;
        return `linear-gradient(90deg, ${mapped.map((color, index) => `${color} ${index * step}% ${(index + 1) * step}%`).join(', ')})`;
      }

      const containedKey = Object.keys(colorMap).find((key) => normalized.includes(key));
      return colorMap[normalized] || mapped[0] || (containedKey ? colorMap[containedKey] : '#d8d8d8');
    }

    async addToCart(event, form, button) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      if (!form || !button || button.disabled || button.classList.contains('loading')) return;

      button.classList.add('loading');
      button.setAttribute('aria-disabled', 'true');

      const cartDrawer = document.querySelector('cart-drawer');
      const formData = new FormData(form);

      if (cartDrawer && typeof cartDrawer.getSectionsToRender === 'function') {
        const sectionIds = cartDrawer.getSectionsToRender().map((section) => section.id).filter(Boolean);
        if (sectionIds.length) formData.append('sections', sectionIds.join(','));
        formData.append('sections_url', window.location.pathname);
      }

      try {
        const response = await fetch((window.routes && window.routes.cart_add_url) || '/cart/add.js', {
          method: 'POST',
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
            Accept: 'application/json'
          },
          body: formData
        });

        const parsedState = await response.json();
        if (!response.ok || parsedState.status) {
          throw new Error(parsedState.description || parsedState.message || 'Ajout au panier impossible.');
        }

        if (cartDrawer && typeof cartDrawer.renderContents === 'function' && parsedState.sections) {
          cartDrawer.renderContents(parsedState);
        } else {
          await this.refreshCartDrawer(cartDrawer);
        }

        this.openCartDrawer(cartDrawer, button);
      } catch (error) {
        console.error('ARLAYA Complete Set:', error);
        const label = button.querySelector('[data-complete-set-add-label]');
        if (label) {
          const original = label.textContent;
          label.textContent = 'VEUILLEZ RÉESSAYER';
          setTimeout(() => {
            label.textContent = original;
          }, 2200);
        }
      } finally {
        button.classList.remove('loading');
        button.removeAttribute('aria-disabled');
      }
    }

    async refreshCartDrawer(cartDrawer) {
      if (!cartDrawer) return;

      const wrapper = cartDrawer.closest('.shopify-section');
      const sectionId = wrapper?.id?.replace(/^shopify-section-/, '');
      if (!sectionId) return;

      try {
        const separator = window.location.pathname.includes('?') ? '&' : '?';
        const response = await fetch(`${window.location.pathname}${separator}sections=${encodeURIComponent(sectionId)}`, {
          headers: { 'X-Requested-With': 'XMLHttpRequest' }
        });
        if (!response.ok) return;

        const sections = await response.json();
        const html = sections[sectionId];
        if (!html) return;

        const doc = new DOMParser().parseFromString(html, 'text/html');
        const refreshedDrawer = doc.querySelector('cart-drawer');
        if (refreshedDrawer) cartDrawer.innerHTML = refreshedDrawer.innerHTML;
      } catch (error) {
        console.warn('ARLAYA Complete Set: actualisation du panier impossible.', error);
      }
    }

    openCartDrawer(cartDrawer, opener) {
      if (cartDrawer && typeof cartDrawer.open === 'function') {
        setTimeout(() => cartDrawer.open(opener), 30);
        return;
      }

      if (cartDrawer) {
        cartDrawer.classList.add('active');
        document.body.classList.add('overflow-hidden');
        return;
      }

      const cartTrigger = document.querySelector('#cart-icon-bubble, .header__icon--cart, a[href="/cart"]');
      if (cartTrigger) cartTrigger.click();
    }

    bindGuideModal() {
      this.querySelectorAll('[data-complete-set-guide]').forEach((trigger) => {
        trigger.addEventListener('click', () => this.openGuide(trigger));

        const prefetch = () => this.prefetchGuide(trigger);
        trigger.addEventListener('pointerenter', prefetch, { once: true, passive: true });
        trigger.addEventListener('focus', prefetch, { once: true });
        trigger.addEventListener('touchstart', prefetch, { once: true, passive: true });
      });

      this.modal?.querySelectorAll('[data-complete-set-modal-close]').forEach((button) => {
        button.addEventListener('click', () => this.closeGuide());
      });
    }

    scheduleGuidePrefetch() {
      const run = () => {
        this.guidePrefetchHandle = null;
        this.prefetchGuides();
      };

      if ('requestIdleCallback' in window) {
        this.guidePrefetchMode = 'idle';
        this.guidePrefetchHandle = window.requestIdleCallback(run, { timeout: 900 });
      } else {
        this.guidePrefetchMode = 'timeout';
        this.guidePrefetchHandle = window.setTimeout(run, 250);
      }
    }

    prefetchGuides() {
      this.querySelectorAll('[data-complete-set-guide]').forEach((trigger) => {
        this.prefetchGuide(trigger);
      });
    }

    prefetchGuide(trigger) {
      const customGuideImage = trigger?.dataset?.guideImage;
      if (customGuideImage) {
        this.preloadGuideImage(customGuideImage);
        return;
      }

      const productUrl = trigger?.dataset?.productUrl;
      if (!productUrl) return;

      this.loadGuide(productUrl).catch(() => {
        // L’erreur sera affichée uniquement si la cliente ouvre réellement le guide.
      });
    }

    preloadGuideImage(imageUrl) {
      if (!imageUrl) return;

      const image = new Image();
      image.decoding = 'async';
      image.src = imageUrl;
    }

    async loadGuide(productUrl) {
      const cachedGuide = this.guideCache.get(productUrl);
      if (cachedGuide) return cachedGuide;

      const pendingRequest = this.guideRequests.get(productUrl);
      if (pendingRequest) return pendingRequest;

      const request = fetch(productUrl, {
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      })
        .then((response) => {
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          return response.text();
        })
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const guide = doc.querySelector('.sizing-chart__modal');
          if (!guide) throw new Error('Guide des tailles absent de la fiche produit.');

          const clonedGuide = guide.cloneNode(true);
          clonedGuide.querySelectorAll('script, button[id^="ModalClose-"]').forEach((node) => node.remove());
          clonedGuide.querySelectorAll('[id]').forEach((node) => node.removeAttribute('id'));
          this.prepareGuideContent(clonedGuide);

          const renderedGuide = clonedGuide.outerHTML;
          this.guideCache.set(productUrl, renderedGuide);
          return renderedGuide;
        })
        .finally(() => {
          this.guideRequests.delete(productUrl);
        });

      this.guideRequests.set(productUrl, request);
      return request;
    }

    openGuideModal() {
      if (!this.modal) return;

      if (this.modal.getAttribute('aria-hidden') !== 'false') {
        this.previousBodyOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        this.modal.setAttribute('aria-hidden', 'false');
      }

      window.requestAnimationFrame(() => {
        this.modal?.querySelector('.arlaya-complete-set__modal-dialog')?.focus();
      });
    }

    showGuideLoading() {
      if (!this.modalBody) return;

      this.openGuideModal();
      this.modalBody.setAttribute('aria-busy', 'true');
      this.modalBody.innerHTML = `
        <div class="arlaya-complete-set__modal-loading" role="status">
          <span class="arlaya-complete-set__modal-spinner" aria-hidden="true"></span>
          <span>Chargement du guide des tailles…</span>
        </div>
      `;
    }

    showGuideContent(content) {
      if (!this.modalBody) return;

      this.openGuideModal();
      this.modalBody.innerHTML = content;
      this.modalBody.setAttribute('aria-busy', 'false');
    }

    handleEscape(event) {
      if (event.key !== 'Escape') return;

      if (this.activeColorControl) {
        this.closeColorMenu(this.activeColorControl);
        return;
      }

      if (this.modal && this.modal.getAttribute('aria-hidden') === 'false') {
        this.closeGuide();
      }
    }

    async openGuide(trigger) {
      if (!this.modal || !this.modalBody) return;

      const requestId = ++this.guideRequestId;
      this.lastGuideTrigger = trigger;

      const customGuideImage = trigger.dataset.guideImage;
      if (customGuideImage) {
        this.showGuideContent(
          `<img src="${this.escapeAttribute(customGuideImage)}" alt="Guide des tailles" loading="eager" decoding="async">`
        );
        return;
      }

      const productUrl = trigger.dataset.productUrl;
      if (!productUrl) {
        this.showGuideError('#');
        return;
      }

      const cachedGuide = this.guideCache.get(productUrl);
      if (cachedGuide) {
        this.showGuideContent(cachedGuide);
        return;
      }

      const loadingTimer = window.setTimeout(() => {
        if (requestId === this.guideRequestId) this.showGuideLoading();
      }, 160);

      try {
        const renderedGuide = await this.loadGuide(productUrl);
        window.clearTimeout(loadingTimer);
        if (requestId !== this.guideRequestId) return;
        this.showGuideContent(renderedGuide);
      } catch (error) {
        window.clearTimeout(loadingTimer);
        if (requestId !== this.guideRequestId) return;
        console.warn('ARLAYA Complete Set: guide des tailles introuvable.', error);
        this.showGuideError(productUrl);
      }
    }

    prepareGuideContent(guide) {
      guide.querySelectorAll('table').forEach((table) => {
        if (table.parentElement?.classList.contains('arlaya-complete-set__table-scroll')) return;

        const wrapper = document.createElement('div');
        wrapper.className = 'arlaya-complete-set__table-scroll';
        table.parentNode.insertBefore(wrapper, table);
        wrapper.appendChild(table);
      });
    }

    showGuideError(productUrl) {
      const safeUrl = this.escapeAttribute(productUrl || '#');
      this.openGuideModal();
      this.modalBody.setAttribute('aria-busy', 'false');
      this.modalBody.innerHTML = `
        <div class="arlaya-complete-set__modal-error">
          Le guide des tailles de ce produit n’a pas pu être chargé.<br>
          <a href="${safeUrl}" target="_blank" rel="noopener">Ouvrir la fiche du produit</a>
        </div>
      `;
    }

    closeGuide() {
      if (!this.modal) return;
      this.guideRequestId += 1;
      this.modal.setAttribute('aria-hidden', 'true');
      this.modalBody?.setAttribute('aria-busy', 'false');
      document.body.style.overflow = this.previousBodyOverflow;
      this.lastGuideTrigger?.focus();
    }

    formatMoney(cents, fallbackCurrency) {
      const currency = (window.Shopify && Shopify.currency && Shopify.currency.active)
        || fallbackCurrency
        || 'EUR';
      const locale = document.documentElement.lang || 'fr-FR';

      try {
        return new Intl.NumberFormat(locale, {
          style: 'currency',
          currency,
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(Number(cents || 0) / 100);
      } catch (error) {
        return `${(Number(cents || 0) / 100).toFixed(2)} ${currency}`;
      }
    }

    escapeAttribute(value) {
      return String(value || '')
        .replace(/&/g, '&amp;')
        .replace(/"/g, '&quot;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
    }
  }

  customElements.define(TAG_NAME, CompleteSetSection);
})();
